import React, { useEffect, useState } from 'react';
import { repairApi, getRepairImageUrl, vehicleApi } from '../services/api';
import { RepairRecord, CreateRepairRecord, Vehicle } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { formatVietnamCurrency, parseFormattedNumber } from '../utils/numberFormatter';
import ImageViewer from '../components/ImageViewer';

const RepairPage: React.FC = () => {
  const { t } = useLanguage();
  const [records, setRecords] = useState<RepairRecord[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]); // Add vehicles state
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<RepairRecord | null>(null);
  const [formData, setFormData] = useState<CreateRepairRecord>({
    licensePlate: '',
    repairDate: '',
    description: '',
    cost: 0,
    repairShop: '',
    notes: ''
  });
  const [linkFilter, setLinkFilter] = useState<'all' | 'linked' | 'unlinked'>('all');
  // Image upload states
  const [repairImage, setRepairImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  // Image viewer states
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [viewerImageUrl, setViewerImageUrl] = useState<string>('');

  useEffect(() => {
    fetchRecords();
    fetchVehicles(); // Add vehicles fetch
  }, []);

  const fetchRecords = async () => {
    try {
      const response = await repairApi.getAll();
      setRecords(response.data);
    } catch (error) {
      console.error('Error fetching records:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await vehicleApi.getAll();
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Prepare payload with image
      let payload: any = { ...formData };
      if (repairImage) {
        const reader = new FileReader();
        reader.onloadend = async () => {
          payload.ImageBase64 = (reader.result as string).split(',')[1];
          payload.ImageContentType = repairImage.type;
          if (editingRecord) {
            await repairApi.update(editingRecord.id, payload);
          } else {
            await repairApi.create(payload);
          }
          await fetchRecords();
          resetForm();
        };
        reader.readAsDataURL(repairImage);
      } else {
        if (editingRecord) {
          await repairApi.update(editingRecord.id, payload);
        } else {
          await repairApi.create(payload);
        }
        await fetchRecords();
        resetForm();
      }
    } catch (error) {
      console.error('Error saving record:', error);
    }
  };

  const handleEdit = (record: RepairRecord) => {
    setEditingRecord(record);
    setFormData({
      licensePlate: record.licensePlate,
      repairDate: record.repairDate.split('T')[0],
      description: record.description || '',
      cost: record.cost,
      repairShop: record.repairShop || '',
      notes: record.notes || ''
    });
    setShowModal(true);
    setRepairImage(null);
    setImagePreview(getRepairImageUrl(record.imageUrl));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setRepairImage(file);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setImagePreview(null);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      licensePlate: '',
      repairDate: '',
      description: '',
      cost: 0,
      repairShop: '',
      notes: ''
    });
    setEditingRecord(null);
    setShowModal(false);
    setRepairImage(null);
    setImagePreview(null);
  };

  const handleViewImage = (imageUrl: string | undefined) => {
    if (imageUrl) {
      const fullImageUrl = getRepairImageUrl(imageUrl) || '';
      setViewerImageUrl(fullImageUrl);
      setShowImageViewer(true);
    }
  };

  const handleUnlink = async (id: number) => {
    if (window.confirm('Are you sure you want to unlink this repair record?')) {
      try {
        await repairApi.unlink(id);
        alert('Repair record unlinked successfully');
        fetchRecords();
      } catch (error) {
        console.error('Error unlinking repair record:', error);
        alert('Error unlinking repair record');
      }
    }
  };

  if (loading) {
    return <div className="container-fluid mt-4"><div className="spinner-border"></div></div>;
  }

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>{t('repair.records')}</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          {t('add.repair.record')}
        </button>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">Filter by Link Status:</label>
            <select 
              className="form-select" 
              style={{ width: '200px' }}
              value={linkFilter}
              onChange={(e) => setLinkFilter(e.target.value as 'all' | 'linked' | 'unlinked')}
            >
              <option value="all">All Records</option>
              <option value="linked">Linked Records</option>
              <option value="unlinked">Unlinked Records</option>
            </select>
          </div>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('license.plate')}</th>
                  <th>{t('date')}</th>
                  <th>{t('description')}</th>
                  <th>{t('cost')}</th>
                  <th>{t('repair.shop')}</th>
                  <th>Image</th>
                  <th>{t('link.status')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {records.filter(record => {
                  if (linkFilter === 'linked') return !!record.shippingRecordId;
                  if (linkFilter === 'unlinked') return !record.shippingRecordId;
                  return true;
                }).map((record) => (
                  <tr key={record.id}>
                    <td>{record.licensePlate}</td>
                    <td>{new Date(record.repairDate).toLocaleDateString()}</td>
                    <td>{record.description || '-'}</td>
                    <td>{formatVietnamCurrency(record.cost)}</td>
                    <td>{record.repairShop || '-'}</td>
                    <td>
                      {record.imageUrl ? (
                        <button
                          className="btn btn-sm btn-outline-info"
                          onClick={() => handleViewImage(record.imageUrl)}
                          title="View repair image"
                        >
                          📷 View
                        </button>
                      ) : (
                        <span className="text-muted">No image</span>
                      )}
                    </td>
                    <td>
                      {record.shippingRecordId ? (
                        <a 
                          href={`/shipping/edit/${record.shippingRecordId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-info"
                        >
                          {record.shippingRecordInfo || `${t('linked')} #${record.shippingRecordId}`}
                        </a>
                      ) : (
                        <span className="text-muted">{t('unlinked')}</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => handleEdit(record)}
                      >
                        {t('edit')}
                      </button>
                      {record.shippingRecordId && (
                        <button
                          className="btn btn-sm btn-outline-warning"
                          onClick={() => handleUnlink(record.id)}
                        >
                          {t('unlink')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingRecord ? t('edit.repair.record') : t('add.repair.record')}
                </h5>
                <button type="button" className="btn-close" onClick={resetForm}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row mb-3">
                    <div className="col-sm-3">
                      <label className="form-label">{t('license.plate')} *</label>
                    </div>
                    <div className="col-sm-9">
                      <select
                        className="form-select"
                        value={formData.licensePlate}
                        onChange={(e) => setFormData({ ...formData, licensePlate: e.target.value })}
                        required
                      >
                        <option value="">{t('please.select')}</option>
                        {vehicles.map((vehicle) => (
                          <option key={vehicle.id} value={vehicle.licensePlate}>
                            {vehicle.licensePlate} - {vehicle.brand} {vehicle.model}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-sm-3">
                      <label className="form-label">{t('repair.date')} *</label>
                    </div>
                    <div className="col-sm-9">
                      <input
                        type="date"
                        className="form-control"
                        value={formData.repairDate}
                        onChange={(e) => setFormData({ ...formData, repairDate: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-sm-3">
                      <label className="form-label">{t('description')}</label>
                    </div>
                    <div className="col-sm-9">
                      <textarea
                        className="form-control"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-sm-3">
                      <label className="form-label">{t('cost')}</label>
                    </div>
                    <div className="col-sm-9">
                      <input
                        type="text"
                        className="form-control"
                        value={formData.cost === 0 ? '' : formData.cost.toLocaleString('en-US')}
                        onChange={(e) => setFormData({ ...formData, cost: parseFormattedNumber(e.target.value) })}
                        placeholder="Enter repair cost"
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-sm-3">
                      <label className="form-label">{t('repair.shop')}</label>
                    </div>
                    <div className="col-sm-9">
                      <input
                        type="text"
                        className="form-control"
                        value={formData.repairShop}
                        onChange={(e) => setFormData({ ...formData, repairShop: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-sm-3">
                      <label className="form-label">{t('notes')}</label>
                    </div>
                    <div className="col-sm-9">
                      <textarea
                        className="form-control"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  {/* Image Upload Section */}
                  <div className="row mb-3">
                    <div className="col-sm-3">
                      <label className="form-label">Repair Images</label>
                    </div>
                    <div className="col-sm-9">
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*,application/pdf"
                        onChange={handleImageChange}
                      />
                      {imagePreview && (
                        <div className="mt-2">
                          <img
                            src={imagePreview}
                            alt="Repair document preview"
                            className="img-thumbnail"
                            style={{ maxWidth: '200px', maxHeight: '200px' }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={resetForm}>
                    {t('cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingRecord ? t('update') : t('create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {showModal && <div className="modal-backdrop show"></div>}
      
      {/* Enhanced Image Viewer */}
      <ImageViewer
        src={viewerImageUrl}
        alt="Repair Record Image"
        isOpen={showImageViewer}
        onClose={() => setShowImageViewer(false)}
      />
    </div>
  );
};

export default RepairPage;